import { useEffect } from 'react'
import { useAppStore } from '@/store/app-store'
import { useConfigStore } from '@/store/config-store'
import { useAuthStore } from '@/store/auth-store'
import { useCashStore } from '@/store/cash-store'
import LoginScreen from '@/screens/LoginScreen'
import SetupScreen from '@/screens/SetupScreen'
import CaisseScreen from '@/screens/CaisseScreen'
import TablesScreen from '@/screens/TablesScreen'
import ReservationsScreen from '@/screens/ReservationsScreen'
import StatsScreen from '@/screens/StatsScreen'
import TicketsScreen from '@/screens/TicketsScreen'
import BottomNav from '@/components/BottomNav'
import EmployeeBar from '@/components/EmployeeBar'
import Toast from '@/components/Toast'
import FermeOverlay from '@/components/FermeOverlay'
import ForceSettleOverlay from '@/components/ForceSettleOverlay'
import ScannerModal from '@/components/ScannerModal'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useHeartbeat } from '@/hooks/useHeartbeat'
import { useAuthRefresh } from '@/hooks/useAuthRefresh'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'

export default function App() {
  const screen = useAppStore((s) => s.screen)
  const isLoading = useAppStore((s) => s.isLoading)
  const isConfigured = useConfigStore((s) => s.isConfigured())
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  const isFerme = useCashStore((s) => s.isFerme)
  const closingPending = useCashStore((s) => s.closingPending)

  useHeartbeat()
  useAuthRefresh()
  useKeyboardShortcuts()

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }
  }, [])

  // Visual viewport resize handler — adjusts layout when mobile keyboard appears
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const handler = () => {
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${vv.height}px`
      )
    }
    handler()
    vv.addEventListener('resize', handler)
    return () => vv.removeEventListener('resize', handler)
  }, [])

  // Prevent accidental pinch-to-zoom on POS terminal
  useEffect(() => {
    const handler = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }
    document.addEventListener('touchstart', handler, { passive: false })
    return () => document.removeEventListener('touchstart', handler)
  }, [])

  // Auth gate — must log in first
  if (!isAuthenticated) return <LoginScreen />

  // Config gate — POS must be selected after login
  if (!isConfigured) return <SetupScreen />

  const renderScreen = () => {
    switch (screen) {
      case 'caisse': return <CaisseScreen />
      case 'tables': return <TablesScreen />
      case 'reservations': return <ReservationsScreen />
      case 'stats': return <StatsScreen />
      case 'tickets': return <TicketsScreen />
      default: return <CaisseScreen />
    }
  }

  return (
    <div className="h-full flex flex-col">
      <EmployeeBar />
      <main className="flex-1 overflow-hidden">
        {renderScreen()}
      </main>
      <BottomNav />
      <Toast />
      {isFerme && <FermeOverlay />}
      {closingPending && <ForceSettleOverlay />}
      <ScannerModal />
      {isLoading && <LoadingSpinner />}
    </div>
  )
}
